// Which addresses the small-car booking may accept, kept CommonJS and free of
// firebase so scripts/check-service-area.js can require it. Both booking pages
// call it -- bookingDate.vue (bookingSlot / bookingDay) and carBooking.vue
// (booking) -- and both do it the same way: a zipcode's zonedv rows go through
// serviceableRows(), and an SO's free-text custAddress through
// resolveFromAddress().
//
// This is deliberately separate from the `zoneMain` / `zoneDv` fields on a
// zonedv row. Those describe how a delivery is routed once it is accepted and
// are read by managecar and the schedule; this describes whether the small car
// serves the address at all. Do not conflate them.
//
// To open more ground, edit the table below and re-run `npm run test:area`.
// Coverage is described at whichever of the three levels fits:
//   provinces    -- open in full
//   districts    -- an อำเภอ open in full, in a province that is otherwise closed
//   subDistricts -- named ตำบล, in an อำเภอ that is otherwise closed
const SERVICE_AREA = {
  provinces: [
    'กรุงเทพมหานคร',
    'นนทบุรี',
    'ปทุมธานี',
    'สมุทรปราการ'
  ],

  districts: {
    // 'ชลบุรี': ['ศรีราชา'],
  },

  // The 13 ตำบล delivery named. พุทธมณฑล is written out ตำบล by ตำบล even though
  // all three of its ตำบล are open and it could have been one `districts` entry:
  // the table should say what delivery said, so that adding or removing one ตำบล
  // is a one-line edit that nobody has to reason about.
  subDistricts: {
    'นครปฐม': {
      'พุทธมณฑล': ['คลองโยง', 'ศาลายา', 'มหาสวัสดิ์'],
      'สามพราน': ['ทรงคนอง', 'บางกระทึก', 'บางเตย', 'ไร่ขิง', 'กระทุ่มล้ม']
    },
    'สมุทรสาคร': {
      'เมืองสมุทรสาคร': ['พันท้ายนรสิงห์', 'บางน้ำจืด', 'คอกกระบือ'],
      'กระทุ่มแบน': ['แคราย', 'อ้อมน้อย']
    }
  }
}

function clean (value) {
  return String(value === null || value === undefined ? '' : value).trim()
}

// A place is { province, district, subDt } -- the shape of a zonedv row.
//
// -> true  served
//    false not served
//    null  the place is not specific enough to say. That is not the same as a
//          refusal: สมุทรสาคร serves 5 ตำบล out of 79, so "สมุทรสาคร" alone
//          decides nothing, and callers that have a real customer in front of
//          them should ask rather than guess either way.
function judge (place, area) {
  const province = clean(place.province)
  if (!province) return null
  if (area.provinces.indexOf(province) !== -1) return true

  const openDistricts = area.districts[province] || []
  const openSubDistricts = area.subDistricts[province] || {}
  if (!openDistricts.length && !Object.keys(openSubDistricts).length) return false

  const district = clean(place.district)
  if (!district) return null
  if (openDistricts.indexOf(district) !== -1) return true

  const openInDistrict = openSubDistricts[district]
  if (!openInDistrict) return false

  const subDt = clean(place.subDt)
  if (!subDt) return null
  return openInDistrict.indexOf(subDt) !== -1
}

// Strict: anything short of a definite yes is a no. Callers that need to tell
// "no" from "cannot say" go through resolveFromAddress instead.
function isServiceable (place, area = SERVICE_AREA) {
  return judge(place || {}, area) === true
}

// bookingDate stores the chosen place as "subDt,district,province" (the shape
// bookingRules.formatLocation writes). Checked without going through
// bookingRules so this module stays free of its moment-timezone dependency.
function isServiceableLocation (location, area = SERVICE_AREA) {
  const parts = clean(location).split(',')
  if (parts.length !== 3) return false
  return isServiceable({ subDt: parts[0], district: parts[1], province: parts[2] }, area)
}

// The same string as a tri-state, because a flag stored on a booking needs a
// distinction the submit gate does not. isServiceableLocation answers one
// question -- may this be submitted -- and rightly says false for anything
// short of a definite yes. A booking that records WHY it was allowed cannot
// share that answer: "we do not deliver there" is a deliberate out-of-area trip
// staff confirmed, and "this string says nothing" is a form that never
// resolved. Only the first is a trip, and only the first must escape the day's
// car capacity.
function locationArea (location, area = SERVICE_AREA) {
  const parts = clean(location).split(',')
  if (parts.length !== 3) return 'unknown'
  const verdict = judge({ subDt: parts[0], district: parts[1], province: parts[2] }, area)
  return verdict === null ? 'unknown' : (verdict ? 'in' : 'out')
}

// Is this province served in full, as routine? Distinct from isServiceable,
// which answers for one PLACE: นครปฐม is partly open, so a นครปฐม address may
// or may not be serviceable while the province itself is never fully served.
//
// The distinction is what an out-of-area route needs. A route to a fully-served
// province is meaningless -- the small car already goes there every day -- and
// opening one would reserve a truck and pull those deliveries off the day's
// ordinary capacity for nothing. A route to นครปฐม is the opposite: it is
// exactly the ตำบล this table leaves out.
function isFullyServedProvince (province, area = SERVICE_AREA) {
  return area.provinces.indexOf(clean(province)) !== -1
}

// zonedv rows -> the rows the small car will actually deliver to. This is what
// keeps a ตำบล picker honest: for 74000 it offers 3 of the zipcode's 18 ตำบล.
function serviceableRows (rows, area = SERVICE_AREA) {
  return rows.filter(row => isServiceable(row, area))
}

// The area as one Thai line, for the dialogs that explain a rejection. Partly
// open provinces are summarised rather than listed -- naming 13 ตำบล in an
// alert helps nobody, and the ตำบล picker already shows exactly which are open.
function areaLabel (area = SERVICE_AREA) {
  const whole = area.provinces.join(', ')
  const partial = Object.keys(area.districts)
    .concat(Object.keys(area.subDistricts))
    .filter((province, i, all) => all.indexOf(province) === i)
  return partial.length ? `${whole} และบางพื้นที่ของ ${partial.join(', ')}` : whole
}

// SAP hands us custAddress as one free-text line, so zip / ตำบล / อำเภอ have to
// be picked out of it. Same expressions carBooking's addZoneInfo() uses.
const ZIP_RE = /\b\d{5}\b/
const SUBDT_RE = /(?:^|\s)(?:ตำบล|แขวง|เเขวง|ต\.|ตำบล\/แขวง|แขวง\/ตำบล|ตำบลแขวง|แขวงตำบล)\.?\s*([^ ,/]+)/
const DISTRICT_RE = /(?:เขต|อำเภอ|อ\.(?:\s*(?:เขต|อำเภอ))?|เขต\/อำเภอ|อำเภอ\/เขต|เขตอำเภอ|อำเภอเขต)\s*([^ ,/]+)/

function parseAddress (address) {
  const zip = address.match(ZIP_RE)
  const subDt = address.match(SUBDT_RE)
  const district = address.match(DISTRICT_RE)
  return {
    zip: zip ? zip[0] : '',
    subDt: subDt ? subDt[1] : '',
    district: district ? district[1] : ''
  }
}

// Keep a narrowing only when it leaves something behind -- a misspelt ตำบล
// should not throw away a zipcode that resolved perfectly well.
function narrow (rows, predicate) {
  const kept = rows.filter(predicate)
  return kept.length ? kept : rows
}

// Last resort when no zipcode resolved: look for a province name in the text.
// Longest name first, so พระนครศรีอยุธยา is not beaten by a shorter prefix.
function findProvinceName (address, zoneDv) {
  const names = [...new Set(zoneDv.map(row => row.province))]
    .sort((a, b) => b.length - a.length)
  return names.find(name => address.indexOf(name) !== -1) || ''
}

// The province an address names outright -- "จ.ตาก", "จังหวัดตาก" -- and only
// when that is a real province name. Unlike findProvinceName this cannot be
// fooled by a street (ถ.พิษณุโลก is in Bangkok), which is why it may overrule
// a zipcode while findProvinceName may not.
const PROVINCE_RE = /(?:จังหวัด|จ\.)\s*([^\s,/\d]+)/
function namedProvince (address, zoneDv) {
  const m = address.match(PROVINCE_RE)
  if (!m) return ''
  return zoneDv.some(row => row.province === m[1]) ? m[1] : ''
}

// Judge the zonedv rows an address narrowed to.
function judgeRows (rows, parsed, zip, area) {
  const province = rows[0].province
  const district = parsed.district || rows[0].district
  // The ตำบล this address resolved to, when the narrowing left exactly one
  // row. Returned rather than discarded because the SO booking path has no
  // other way to learn it: SAP gives one free-text line, and until this was
  // carried out of here the booking was stored with an empty `location` and
  // the planner could not place it at all.
  const place = rows.length === 1
    ? { subDt: rows[0].subDt, district: rows[0].district, province: rows[0].province }
    : null
  const open = serviceableRows(rows, area)
  if (open.length === rows.length) return { status: 'in', province, district, place, zip }
  if (open.length === 0) return { status: 'out', province, district, place, zip }
  // The zipcode straddles served and unserved ตำบล and the address never said
  // which one -- 74000 covers 18 ตำบล and 3 of them are served. Guessing here
  // would either refuse real work or send a small car to มหาชัย.
  return { status: 'unknown', province, district, place, zip }
}

// -> { status: 'in' | 'out' | 'unknown', province, district, place, zip, source }
// 'unknown' means the address could not be placed, not that it was refused --
// callers ask staff to confirm rather than blocking a real order over a badly
// written address.
//
// opts.city -- the customer's province from SAP's customer master (GetSo
// `city`). Used only when the address itself names no province and no
// zipcode places it, and then reported as source 'city' so the caller can ask
// staff: an address field holding a reference code (a HomePro SO carried
// "S027261013003250185") otherwise leaves a branch in ขอนแก่น unplaceable.
function resolveFromAddress (custAddress, zoneDv, area = SERVICE_AREA, opts = {}) {
  const address = clean(custAddress)
  const parsed = parseAddress(address)
  const named = namedProvince(address, zoneDv)

  let rows = parsed.zip ? zoneDv.filter(row => String(row.zip) === parsed.zip) : []
  // A zipcode that contradicts the province the address names is the typo, not
  // the province: "อ.เมืองตาก จ.ตาก 92000" carried ตรัง's code and was routed
  // to the south. Drop it and place the address by its province instead.
  if (rows.length && named) rows = rows.filter(row => row.province === named)
  if (rows.length && parsed.subDt) rows = narrow(rows, row => row.subDt === parsed.subDt)
  if (rows.length && parsed.district) rows = narrow(rows, row => row.district === parsed.district)
  if (rows.length) return Object.assign(judgeRows(rows, parsed, parsed.zip, area), { source: 'zip' })

  // No zipcode to go by, but a named province and a ตำบล in it: that is as good
  // as a zipcode, and better than a wrong one.
  if (named && parsed.subDt) {
    let inProvince = zoneDv.filter(row => row.province === named && row.subDt === parsed.subDt)
    if (inProvince.length && parsed.district) inProvince = narrow(inProvince, row => row.district === parsed.district)
    if (inProvince.length === 1) {
      return Object.assign(judgeRows(inProvince, parsed, String(inProvince[0].zip), area), { source: 'address' })
    }
  }

  let province = named || findProvinceName(address, zoneDv)
  let source = 'address'
  if (!province) {
    const city = clean(opts.city)
    if (city && zoneDv.some(row => row.province === city)) {
      province = city
      source = 'city'
    }
  }
  if (!province) return { status: 'unknown', province: '', district: parsed.district, place: null, zip: parsed.zip, source: '' }

  const verdict = judge({ province, district: parsed.district, subDt: parsed.subDt }, area)
  return {
    status: verdict === null ? 'unknown' : (verdict ? 'in' : 'out'),
    province,
    district: parsed.district,
    place: null,
    zip: parsed.zip,
    source
  }
}

module.exports = {
  SERVICE_AREA,
  // Exported so geo.js can read a ตำบล out of the same free-text line with the
  // same expressions. Two parsers that disagree about where an address is would
  // be worse than one that is occasionally wrong.
  parseAddress,
  isServiceable,
  serviceableRows,
  isServiceableLocation,
  isFullyServedProvince,
  locationArea,
  areaLabel,
  resolveFromAddress
}
